import { createContext, FC, useEffect, useContext, useMemo, useRef, useState } from "react";
import { crudGetOne, Record, ReduxState } from "ra-core";
import withCompositeResource, { WrappedComponentProps } from "~/components/recource/CompositeResource";
import { AlertAndTime, EncryptionDashboardResponseDto } from "~/gen/api/encrypt";
import { useDispatch, useSelector } from "react-redux";
import { matches } from "lodash";
import { crudGetOneEx } from "~/actions/crudFetchEx";

const EncryptionCompositeContent: FC<WrappedComponentProps> = ({
  children,
  tabs,
  options,
}) => {
  const [alerts, setAlerts] = useState([] as AlertAndTime[])
  return (
    <ActionableAlertsContext.Provider value={useMemo(() => ({ alerts, setAlerts }), [alerts])}>
      {children}
    </ActionableAlertsContext.Provider>
  )
}

export const EncryptionComposite = withCompositeResource(EncryptionCompositeContent);

export const ActionableAlertsContext = createContext({
  alerts: [] as AlertAndTime[],
  setAlerts: (() => {}) as (x:AlertAndTime[]) => void,
})

type SimpleSaveAction = (record: Partial<Record>, callbacks?: any) => void;

export const useSuppressAlertUponSave = <T extends SimpleSaveAction|Record>(saveFn: T, alertLike: Partial<AlertAndTime>): T extends Function ? SimpleSaveAction : Record => {
  const { alerts, setAlerts } = useContext(ActionableAlertsContext)
  const [states, setStates] = useState({
    loading: false,
    loaded: alerts.length > 0,
  })

  const dispatch = useDispatch()
  useEffect(() => {
    if (!states.loaded && !states.loading) {
      dispatch(crudGetOneEx('encryption/dashboard', -1, ''))
      setStates(v => ({...v, loading: true }))
    }
  }, [states.loaded, states.loading, dispatch])

  useSelector<ReduxState, any>(store => {
    if (states.loaded) { return }
    const data = store.admin.resources['encryption/dashboard']?.data || {}
    const alertData : EncryptionDashboardResponseDto|undefined = data[-1] as any
    if (!alertData) { return }
    setTimeout(() => {
      setAlerts(alertData.encryptionAlerts || [])
      setStates(v => ({...v, loading: false, loaded: true }))
    })
  })

  const {current:cache} = useRef({ newPayload: null as any, sig: '' as string })
  if (typeof(saveFn) === 'function') {
    const oldeSaveFn : SimpleSaveAction = saveFn as any
    const newSaveFn = (payload: Record, ...rest: any[]) => {
      const targetAlert = alerts.find(matches(alertLike))
      const newPayload = targetAlert ? {...payload, suppressAlert: targetAlert.receipt } : payload
      oldeSaveFn(newPayload, ...rest)
    }
    return newSaveFn as any
  } else {
    const payload = saveFn
    if (cache.sig === JSON.stringify(payload)) { return cache.newPayload }
    const targetAlert = alerts.find(matches(alertLike))
    const newPayload = targetAlert ? {...payload, suppressAlert: targetAlert.receipt } : payload
    cache.sig = JSON.stringify(payload)
    cache.newPayload = newPayload
    return newPayload as any
  }
}
